/* eslint-disable complexity */
import { formatNameFirstLast } from '@strata/helper-functions';
import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate, useParams } from 'react-router';

import { PasswordEntry } from 'components/PasswordEntry';
import { DeleteUserDialog } from 'pages/userManagement/dialogs/DeleteUserDialog';
import { CREATE_ROUTE, UserType } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { filterPayerRoles, handleDuplicateUserToast } from 'services/UserSupport';
import { getPayerUserSchema } from 'services/Validators';

export const PayerUserDetails = () => {
    const { upn } = useParams();
    const navigate = useNavigate();
    const { enableConfidentialPayerRequests } = useFlags();

    const goHome = () => navigate(`/user-management/${UserType.PAYER}`);

    const isCreate = upn === CREATE_ROUTE;

    const user = useGet(!isCreate ? `/authorization/payer_users/${upn}` : null);
    const userPayers = (user?.payers ?? []).map((payer) => payer.payer_id);
    const userSegments = (user?.payers ?? []).flatMap((payer) => payer.segment_ids);

    const pbms = useGet('/pbm/pbm_info/list')?.map((pbm) => ({
        name: pbm.pbm_name,
        pbm_id: pbm.pbm_id,
    }));

    const payers = useGet('/reference/payers/');
    const payerOptions = payers?.map((payer) => ({
        ...payer,
        name: payer.payer_name,
        secondaryData: payer.payer_id,
    }));

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentOptions = payerSegments?.map((segment) => ({
        ...segment,
        name: segment.segment_name,
        secondaryData: segment.segment_id,
    }));

    const roles = useGet('/authorization/roles/');
    const roleOptions = filterPayerRoles(roles);

    const isLoading = !(isCreate || user) || !(pbms && payers && payerSegments && roles);

    return (
        <Formik
            initialValues={{
                upn: user?.upn,
                full_name: user?.full_name,
                pbm: pbms?.find((pbm) => pbm.pbm_id === user?.pbm_id),
                payers: payerOptions?.filter((payer) => userPayers.includes(payer.payer_id)),
                payer_segments: (payerSegmentOptions ?? []).filter(
                    (segment) =>
                        userPayers.includes(segment.payer_id) &&
                        userSegments.includes(segment.segment_id),
                ),
                role: roleOptions?.find((role) => role.role_name === user?.role_name),
                restricted_access: user?.restricted_access ?? false,
                analytics_access: user?.analytics_access ?? false,
            }}
            enableReinitialize
            validationSchema={getPayerUserSchema(isCreate)}
            onSubmit={async (values) => {
                try {
                    let createResult;
                    const sharedValues = {
                        pbm_id: values.pbm?.pbm_id,
                        payers: values.payers.map((payer) => ({
                            payer_id: payer.payer_id,
                            segment_ids: values.payer_segments
                                .filter((segment) => payer.payer_id === segment.payer_id)
                                .map((segment) => segment.segment_id),
                        })),
                        role_name: values.role.role_name,
                        restricted_access: values.restricted_access,
                        analytics_access: values.analytics_access,
                    };

                    // The payer_id and segment_ids fields act as the primary payer and segments for single payer users
                    // They act as the initial default payer and segments for multi-payer users
                    // Pick the first payer and segments to use when creating a new user
                    // or for when the user's payer is no longer in the list
                    const firstPayerId = values.payers[0].payer_id;
                    const firstPayerSegments = values.payer_segments
                        .filter((segment) => segment.payer_id === firstPayerId)
                        .map((segment) => segment.segment_id);

                    if (isCreate) {
                        createResult = await post('/authorization/payer_users/', {
                            ...sharedValues,
                            payer_id: firstPayerId,
                            segment_ids: firstPayerSegments,
                            upn: values.upn,
                            given_name: values.given_name,
                            family_name: values.family_name,
                            name: formatNameFirstLast(values.given_name, values.family_name),
                            password: values.password,
                        });
                    } else {
                        let payerId;
                        let segmentIds;
                        // If the user's associated payer is still in the list, use it
                        if (values.payers.map((payer) => payer.payer_id).includes(user.payer_id)) {
                            payerId = user.payer_id;
                            // Update the segments list to match the new list
                            segmentIds = values.payer_segments
                                .filter((segment) => segment.payer_id === payerId)
                                .map((segment) => segment.segment_id);
                        } else {
                            // Otherwise, use the first payer and segments
                            payerId = firstPayerId;
                            segmentIds = firstPayerSegments;
                        }
                        await put(`/authorization/payer_users/${values.upn}`, {
                            ...sharedValues,
                            payer_id: payerId,
                            segment_ids: segmentIds,
                            full_name: values.full_name,
                        });
                    }

                    if (createResult?.authzero_user_created === false) {
                        handleDuplicateUserToast(values.upn, UserType.PAYER);
                    } else {
                        SuccessToast({
                            message: `Payer User: ${values.upn} ${
                                isCreate ? 'created' : 'updated'
                            } successfully.`,
                        });
                    }
                    goHome();
                } catch (err) {
                    handleError('Error saving user.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    isLoading={isLoading}
                    formTitle={`${isCreate ? 'Create' : 'Edit'} Payer User`}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formHeaderRightContent={
                        !isCreate && (
                            <DeleteUserDialog userType={UserType.PAYER} onDeleteSuccess={goHome} />
                        )
                    }
                    formLeftContent={
                        <>
                            <TextInputFormik
                                label="User Email"
                                id="upn"
                                disabled={!isCreate}
                                required
                            />
                            {isCreate ? (
                                <>
                                    <TextInputFormik label="First Name" id="given_name" required />
                                    <TextInputFormik label="Last Name" id="family_name" required />
                                    <PasswordEntry userType={UserType.PAYER} />
                                </>
                            ) : (
                                <TextInputFormik label="Full Name" id="full_name" required />
                            )}
                            {enableConfidentialPayerRequests && (
                                <SuggestSingleSelectFormik id="pbm" label="PBM" values={pbms} />
                            )}
                            <SuggestMultiSelectFormik
                                label="Payers"
                                id="payers"
                                required
                                values={payerOptions}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        payers: newValue,
                                        payer_segments: formik.values.payer_segments.filter(
                                            (segment) =>
                                                newValue
                                                    .map((payer) => payer.payer_id)
                                                    .includes(segment.payer_id),
                                        ),
                                    });
                                }}
                            />
                            <SuggestMultiSelectFormik
                                id="payer_segments"
                                label="Payer Segments"
                                required
                                values={payerSegmentOptions?.filter((segment) =>
                                    formik.values.payers
                                        ?.map((payer) => payer.payer_id)
                                        .includes(segment.payer_id),
                                )}
                            />
                            <SuggestSingleSelectFormik
                                id="role"
                                label="Role"
                                required
                                values={roleOptions}
                                helperText={formik.values.role?.description}
                            />
                            <RadioButtonGroupFormik
                                name="restricted_access"
                                label="Restricted Referral Access?"
                                options={['Yes', 'No']}
                                selectedValue={formik.values.restricted_access ? 'Yes' : 'No'}
                                onChange={(newValue) =>
                                    formik.setFieldValue('restricted_access', newValue === 'Yes')
                                }
                            />
                            <RadioButtonGroupFormik
                                name="analytics_access"
                                label="Analytics Access?"
                                options={['Yes', 'No']}
                                selectedValue={formik.values.analytics_access ? 'Yes' : 'No'}
                                onChange={(newValue) =>
                                    formik.setFieldValue('analytics_access', newValue === 'Yes')
                                }
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
